import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";

import Grid from "@mui/material/Grid2";
import totalCustomers from "../../assets/images/dashboard/total-customers.png";
import income from "../../assets/images/dashboard/income.png";
import planReputation from "../../assets/images/dashboard/plan-reputation.png";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Pie, Line } from "react-chartjs-2";
import useWindowSize from "../../hooks/useWindowSize";
import { get } from "../../services/apiService";
import CircularLoader from "../../components/common/ScreenLoader";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartDataLabels,
  PointElement,
  LineElement,
  Title,
  Filler
);

const Dashboard = () => {
  const { width } = useWindowSize();
  const [dashboardData, setDashboardData] = useState([]);
  const [dashboardObject, setDashboardObject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lineChartData, setLineChartData] = useState({
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ], // X-axis labels
    datasets: [
      {
        label: "Customer Growth Rate",
        data: [], // Y-axis values
        fill: true, // Enable area fill
        backgroundColor: "#c4dff7", // Area color (light blue)
        borderColor: "rgba(0, 112, 192, 1)", // Line color (blue)
        borderWidth: 3, // Line thickness
        tension: 0.4, // Controls line smoothness (0.4 is recommended for smooth curves)
        pointRadius: 0,
      },
    ],
  });
  const [pieData, setPieData] = useState({
    labels: [
      "Registered with our domain",
      "Registered with different registrar",
    ],
    datasets: [
      {
        data: [],
        backgroundColor: ["#748BDC", "#D3CEFB"],
        hoverBackgroundColor: ["#748BDC", "#D3CEFB"],
      },
    ],
  });

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const transformResponse = (dashboardObject) => {
    const totalCustomersIcon = totalCustomers; // Replace with actual icon
    const incomeIcon = income;
    const planReputationIcon = planReputation;
    // Extract values from the input array

    // Format output
    return [
      {
        value: dashboardObject.totalCustomersCount,
        label: "Total Customers",
        icon: totalCustomersIcon,
      },
      { value: dashboardObject.totalIncome, label: "Income", icon: incomeIcon },
      { value: 200, label: "Plan Reputation", icon: planReputationIcon },
      {
        value: dashboardObject.totalUsersCount,
        label: "Active Users",
        icon: totalCustomersIcon,
      },
      {
        value: dashboardObject.totalMerchants,
        label: "Merchants",
        icon: totalCustomersIcon,
      }, // Static value, adjust as needed
    ];
  };
  const mergeArrayToObject = (data) => {
    return data.reduce((acc, obj) => {
      return { ...acc, ...obj };
    }, {});
  };

  const getAllStatistics = async () => {
    const apiEndpoints = [
      "reports/total-customers",
      "reports/total-income",
      "reports/domains-result",
      "reports/total-users",
      "reports/total-merchants",
      "reports/customers-by-month",
    ];

    try {
      // Make all API calls simultaneously

      const responses = await Promise.all(
        apiEndpoints.map((endpoint) => get(endpoint, {}, onLoading))
      );

      // Extract data from responses
      const results = responses.map((response) => response.data);

      const dashboardResObject = mergeArrayToObject(results);
      const { jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec } =
        dashboardResObject.customersByMonth;
      setPieData((prevData) => ({
        ...prevData,
        datasets: [
          {
            ...prevData.datasets[0],
            data: [
              dashboardResObject.microbandCount,
              dashboardResObject.nonMicrobandCount,
            ],
          },
        ],
      }));
      setLineChartData({
        ...lineChartData,
        datasets: [
          {
            ...lineChartData.datasets[0],
            data: [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec],
          },
        ],
      });
      setDashboardObject(dashboardObject);
      const finalRes = transformResponse(dashboardResObject);
      setDashboardData(finalRes);
      return results;
    } catch (error) {
      console.error("Error fetching statistics:", error);
      return [];
    }
  };

  useEffect(() => {
    getAllStatistics();
  }, []);
  // const summaryCards = [
  //   { value: "1000", label: "Total Customers", icon: totalCustomers },
  //   { value: "49,832", label: "Income", icon: income },
  //   { value: "$12,396", label: "Plan Reputation", icon: planReputation },
  //   { value: "90", label: "Active Users", icon: totalCustomers },
  //   { value: "100", label: "Merchants", icon: totalCustomers },
  // ];

  const pieChartOptions = {
    plugins: {
      legend: {
        position: width > 1200 ? "right" : "top",
        labels: {
          generateLabels: (chart) => {
            const { data } = chart;
            return data.labels.map((label, index) => {
              const value = data.datasets[0].data[index];
              const color = value === 40 ? "#748BDC" : "#D3CEFB";
              return {
                text: label,
                fillStyle: color, // Set color dynamically based on value
                fontColor: color, // Set default font color
              };
            });
          },
          font: {
            size: 18,
          },
        },
      },

      datalabels: false,
    },
  };

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: "top", // Legend position
      },
      title: {
        display: true,
        text: `Customer's Growth Rate ${new Date().getFullYear()}`, // Chart title
      },
      tooltip: {
        enabled: true, // Disable tooltips to hide data on hover
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // Hide grid lines on X-axis
        },
      },
      y: {
        grid: {
          display: false, // Hide grid lines on X-axis
        },
        beginAtZero: true, // Start Y-axis at 0
      },
    },
  };

  return (
    <>
      <Box sx={{ p: width > 450 ? 3 : 1 }}>
        {/* Summary Section */}
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12 }}>
            <Card sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Box
                sx={{
                  borderRadius: "50%",
                  marginRight: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    color: "#0A0A0A",
                    fontSize: width > 900 ? "26px" : "18px",
                    fontWeight: 700,
                  }}
                >
                  Dashboard
                </Typography>
              </Box>
            </Card>
          </Grid>

          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <Grid
                  item
                  size={{ lg: 4, md: 6, xs: 12 }}
                  sx={{
                    display: "flex", // Ensures proper alignment
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "122px",
                  }}
                >
                  <Card
                    sx={{
                      // p: 2,
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <CircularLoader />
                  </Card>
                </Grid>
              ))
            : dashboardData?.map((card, index) => (
                <Grid item size={{ lg: 4, md: 6, xs: 12 }} key={index}>
                  <Card
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: "24px",
                          fontFamily: "Tajawal-Bold",
                          fontWeight: 700,
                          color: "#202020",
                        }}
                      >
                        {card.label === "Income"
                          ? `${card.value}  SAR`
                          : card.value}
                      </Typography>
                      <Typography
                        color="textSecondary"
                        sx={{
                          fontSize: "16px",
                          fontFamily: "Tajawal-Regular",
                          fontWeight: 400,
                          color: "#757575",
                        }}
                      >
                        {card.label}
                      </Typography>
                    </CardContent>
                    <Box
                      sx={{
                        backgroundColor:
                          index === 0
                            ? "#e8f5e9"
                            : index === 1
                              ? "#e2ebfd"
                              : index === 2
                                ? "#ffdbcf"
                                : index === 3
                                  ? "#c4dff7"
                                  : "#D3CEFB",
                        borderRadius: "50%",
                        width: 120, // Oval width
                        height: 120, // Oval height
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginLeft: "5%",
                      }}
                    >
                      <img
                        src={card.icon}
                        style={{ height: "30px", width: "30px" }}
                        alt={card.label}
                      />
                    </Box>
                  </Card>
                </Grid>
              ))}

          {/* Pie Chart */}
          <Grid item size={{ lg: 4, md: 6, xs: 12 }}>
            <Card sx={{ p: 2, height: width > 900 ? "100%" : "122px" }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <CircularLoader />
                </Box>
              ) : (
                <Box
                  sx={{
                    // Overall container height
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    position: "relative", // Position for absolute positioning
                  }}
                >
                  {/* Labels on the extreme left */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "0", // Position labels to the extreme left
                      top: "46%",
                      transform: "translateY(-50%)", // Vertically center the labels
                      paddingLeft: "10px",
                      textAlign: "left",
                    }}
                  >
                    {pieData.labels.map((label, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box
                          sx={{
                            width: "16px",
                            height: "16px",
                            backgroundColor:
                              pieData.datasets[0].backgroundColor[index],
                            marginRight: "8px",
                          }}
                        ></Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#202020",
                            fontFamily: "Tajawal-Regular",
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Pie chart at the extreme right */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "53%", // Move chart to the extreme right
                      top: "50%",
                      transform: "translateY(-50%)", // Vertically center the chart
                      height: "85%", // Set chart height to 40% of the container
                      width: "70%", // Optional: adjust width proportionally
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Pie
                      data={pieData}
                      options={{
                        ...pieChartOptions,
                        plugins: {
                          ...pieChartOptions.plugins,
                          legend: {
                            display: false, // Disable default legend
                          },
                        },
                        maintainAspectRatio: false, // Allow independent height adjustment
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Growth Chart Section */}

        <Box mt={3} sx={{ height: "400px", width: "100%" }}>
          <Card sx={{ p: 2, height: "400px", width: "100%" }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <CircularLoader />
              </Box>
            ) : (
              <Line data={lineChartData} options={lineChartOptions} />
            )}
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
